// Slice-internal Konva renderer for sticky notes (TR5 / TR10).
//
// Extracted 1:1 from `objects/board-object-renderer.ts` (createStickyNode /
// updateStickyNode branch). The constants (STICKY_PADDING, STICKY_FONT_*,
// STICKY_LINE_HEIGHT, computeStickyFontSize, stickyPaddingFor) are co-located
// here so the slice owns its rendering knobs end-to-end. The legacy renderer
// keeps re-exports until C3 finishes the registry-fallback migration.

import Konva from 'konva';
import { fitFontSize, type MeasureFn } from '../../core/text/fit-text.helper.js';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { StickyNote } from './sticky.interface.js';
import { BOARD_TEXT_LINE_HEIGHT } from '../../constants/whiteboard-config.constants.js';

const STICKY_BG_NAME = 'sticky-bg';
const STICKY_TEXT_NAME = 'sticky-text';
const STICKY_FOLD_NAME = 'sticky-fold';

/**
 * Ratio used to derive the folded-corner triangle size from the smallest
 * sticky dimension. Gives the same 20 px appearance at the 180-unit default.
 */
const STICKY_FOLD_RATIO = 20 / 180;

/** Reference padding constant (Welteinheiten) bei Standardgröße 180×180. */
export const STICKY_PADDING = 20;

/**
 * Proportionaler Innenabstand für eine Haftnotiz der gegebenen Abmessungen.
 * Basiert auf dem kleineren der beiden Werte (Breite/Höhe), damit auch
 * querformatige Haftnotizen vernünftig aussehen.
 */
export function stickyPaddingFor(w: number, h: number): number {
  return Math.min(w, h) * (STICKY_PADDING / 180);
}

// Board-Hausschrift: D-DIN (horstplan) zuerst, System-Schrift als Fallback,
// falls die Webfont (noch) nicht geladen ist. Muss mit den anderen
// Board-Schrift-Konstanten (PERSON_FONT_FAMILY, textbox FONT_FAMILY)
// uebereinstimmen.
export const STICKY_FONT_FAMILY = '"D-DIN", system-ui, -apple-system, sans-serif';
export const STICKY_FONT_MIN = 10;
export const STICKY_FONT_MAX = 64;

/**
 * Line-height for the sticky text. We use the same value in Konva.Text
 * and in the contentEditable overlay so both renderers agree on how much
 * vertical space a given font size needs. The board-wide text line height.
 */
export const STICKY_LINE_HEIGHT = BOARD_TEXT_LINE_HEIGHT;

/**
 * Shared auto-fit entry point: returns the largest font size at which
 * `text` still fits into the sticky's inner area (width/height minus
 * padding).
 */
export function computeStickyFontSize(width: number, height: number, text: string): number {
  const pad = stickyPaddingFor(width, height);
  const innerW = Math.max(0, width - pad * 2);
  const innerH = Math.max(0, height - pad * 2);
  const measure: MeasureFn = (value, fontSize, maxWidth) => {
    const probe = new Konva.Text({
      text: value,
      fontSize,
      fontFamily: STICKY_FONT_FAMILY,
      lineHeight: STICKY_LINE_HEIGHT,
      width: maxWidth,
      wrap: 'word',
      align: 'left',
    });
    const w = probe.width();
    const h = probe.height();
    probe.destroy();
    return { width: w, height: h };
  };
  const fitted = fitFontSize(text, innerW, innerH, measure, {
    min: STICKY_FONT_MIN,
    max: STICKY_FONT_MAX,
    step: 1,
  });
  // Keine Worttrennung mitten im Wort: Konva (`wrap: 'word'`) bricht ein Wort nur
  // dann um, wenn es allein breiter als die Zeile ist. Wir begrenzen die Schrift
  // daher so, dass auch das BREITESTE einzelne Wort noch in eine Zeile passt —
  // dann tritt der Zeichen-Umbruch gar nicht erst auf. Ein extrem langes Wort
  // verkleinert so die Schrift der ganzen Notiz, statt zerschnitten zu werden.
  const wordCap = longestWordFontCap(text, innerW);
  return Math.max(STICKY_FONT_MIN, Math.min(fitted, wordCap));
}

/**
 * Largest font size at which the widest single word in `text` still fits into
 * `innerW` on ONE line. Text width scales linearly with font size, so we measure
 * each word once at a reference size and scale. A small safety factor absorbs the
 * difference between Konva's text metrics (render) and the browser's (edit
 * overlay), so neither side breaks a word the other keeps whole. Returns
 * `STICKY_FONT_MAX` when there is no measurable word (the cap is then inert).
 */
function longestWordFontCap(text: string, innerW: number): number {
  if (innerW <= 0) return STICKY_FONT_MAX;
  const words = text.split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return STICKY_FONT_MAX;
  const REF = 100;
  let maxWordWidth = 0;
  for (const word of words) {
    const probe = new Konva.Text({
      text: word,
      fontSize: REF,
      fontFamily: STICKY_FONT_FAMILY,
      lineHeight: STICKY_LINE_HEIGHT,
    });
    maxWordWidth = Math.max(maxWordWidth, probe.width());
    probe.destroy();
  }
  if (maxWordWidth <= 0) return STICKY_FONT_MAX;
  const SAFETY = 0.97;
  return Math.floor((REF * innerW * SAFETY) / maxWordWidth);
}

/** Build a fresh Konva.Group rendering the sticky note. */
export function createNode(obj: StickyNote, _ctx: SliceRenderContext): Konva.Group {
  const group = new Konva.Group({
    id: obj.id,
    x: obj.x,
    y: obj.y,
    width: obj.width,
    height: obj.height,
    rotation: obj.rotation,
    name: 'sticky',
  });

  const bg = new Konva.Rect({
    name: STICKY_BG_NAME,
    x: 0,
    y: 0,
    width: obj.width,
    height: obj.height,
    fill: obj.color,
    shadowColor: '#000',
    shadowBlur: 14,
    shadowOffset: { x: 3, y: 5 },
    shadowOpacity: 0.28,
    cornerRadius: [2, 2, 2, 2],
  });

  const foldSize = Math.min(obj.width, obj.height) * STICKY_FOLD_RATIO;
  const stickyPad = stickyPaddingFor(obj.width, obj.height);

  const fold = new Konva.Line({
    name: STICKY_FOLD_NAME,
    points: [
      obj.width - foldSize, obj.height,
      obj.width,            obj.height - foldSize,
      obj.width,            obj.height,
    ],
    closed: true,
    fill: 'rgba(0,0,0,0.10)',
    stroke: 'rgba(0,0,0,0.08)',
    strokeWidth: 0.5,
    listening: false,
  });

  const text = new Konva.Text({
    name: STICKY_TEXT_NAME,
    x: stickyPad,
    y: stickyPad,
    width: Math.max(0, obj.width - stickyPad * 2),
    height: Math.max(0, obj.height - stickyPad * 2),
    text: obj.text,
    fontSize: 16,
    fontFamily: STICKY_FONT_FAMILY,
    lineHeight: STICKY_LINE_HEIGHT,
    fill: '#1f2937',
    align: 'left',
    verticalAlign: 'top',
    wrap: 'word',
  });

  group.add(bg);
  group.add(fold);
  group.add(text);
  applyAutoFit(text, obj);
  return group;
}

/** Apply changed properties from an updated StickyNote onto its existing node. */
export function updateNode(node: Konva.Node, obj: StickyNote, _ctx: SliceRenderContext): void {
  if (!(node instanceof Konva.Group)) return;
  // Base geometry (group x/y/width/height/rotation) is applied centrally by
  // `applyBaseGeometry`; this method only refreshes the inner background,
  // fold and text from the new size.
  const bg = node.findOne<Konva.Rect>(`.${STICKY_BG_NAME}`);
  const fold = node.findOne<Konva.Line>(`.${STICKY_FOLD_NAME}`);
  const text = node.findOne<Konva.Text>(`.${STICKY_TEXT_NAME}`);
  const foldSize = Math.min(obj.width, obj.height) * STICKY_FOLD_RATIO;
  const stickyPad = stickyPaddingFor(obj.width, obj.height);
  if (bg) {
    bg.width(obj.width);
    bg.height(obj.height);
    bg.fill(obj.color);
  }
  if (fold) {
    fold.points([
      obj.width - foldSize, obj.height,
      obj.width,            obj.height - foldSize,
      obj.width,            obj.height,
    ]);
  }
  if (text) {
    text.x(stickyPad);
    text.y(stickyPad);
    text.width(Math.max(0, obj.width - stickyPad * 2));
    text.height(Math.max(0, obj.height - stickyPad * 2));
    text.text(obj.text);
    applyAutoFit(text, obj);
  }
}

function applyAutoFit(text: Konva.Text, obj: StickyNote): void {
  text.fontSize(computeStickyFontSize(obj.width, obj.height, obj.text));
}
